import React from 'react';
import { Helmet } from 'react-helmet';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

// WhatsApp config (shared across the site)
const WHATSAPP_PHONE = '522292072733';

const buildWhatsAppHref = (message) =>
  `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;

const services = [
  {
    id: 'combustion',
    title: 'Renta de soldadoras de combustión',
    description:
      'Equipos portátiles a gasolina o diésel, ideales para obras sin acceso a energía eléctrica.',
    image:
      'https://images.unsplash.com/photo-1727098730153-6408da60d89d?auto=format&fit=crop&w=900&q=80',
    message:
      'Hola GENSOL, me interesa rentar una soldadora de combustión. ¿Me pueden enviar una cotización?',
  },
  {
    id: 'electricas',
    title: 'Renta de soldadoras eléctricas',
    description:
      'Soldadoras industriales de alto rendimiento para talleres, manufactura y montaje estructural.',
    image:
      'https://images.pexels.com/photos/34194569/pexels-photo-34194569.jpeg?auto=compress&cs=tinysrgb&w=900',
    message:
      'Hola GENSOL, me interesa rentar una soldadora eléctrica. ¿Me pueden enviar una cotización?',
  },
  {
    id: 'mantenimiento',
    title: 'Reparación y mantenimiento a soldadoras',
    description:
      'Servicio técnico especializado: diagnóstico, refacciones y mantenimiento preventivo o correctivo.',
    image:
      'https://images.pexels.com/photos/257700/pexels-photo-257700.jpeg?auto=compress&cs=tinysrgb&w=900',
    message:
      'Hola GENSOL, necesito servicio de reparación o mantenimiento para una soldadora. ¿Me pueden ayudar?',
  },
];

const SoldadorasPage = () => {
  return (
    <>
      <Helmet>
        <title>Renta de Soldadoras de Combustión y Eléctricas | GENSOL</title>
        <meta
          name="description"
          content="Renta de soldadoras de combustión, soldadoras eléctricas y servicio de reparación y mantenimiento en Veracruz y sureste de México. Cotiza por WhatsApp."
        />
        <meta
          name="keywords"
          content="renta soldadoras combustión, renta soldadoras eléctricas, mantenimiento soldadoras, reparación soldadoras, Veracruz"
        />
        <link rel="canonical" href="https://www.gensolver.com/servicios/soldadoras" />
        <meta property="og:title" content="Renta de Soldadoras | GENSOL" />
        <meta
          property="og:description"
          content="Soldadoras de combustión, eléctricas y servicio de reparación. Cotiza por WhatsApp."
        />
        <meta property="og:url" content="https://www.gensolver.com/servicios/soldadoras" />
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: 'Inicio', url: 'https://www.gensolver.com/' },
          { name: 'Servicios', url: 'https://www.gensolver.com/servicios' },
          { name: 'Soldadoras', url: 'https://www.gensolver.com/servicios/soldadoras' },
        ]}
      />

      <main
        id="soldadoras"
        data-page="soldadoras"
        data-analytics-page="soldadoras"
        className="pt-24"
      >
        {/* Page Header */}
        <section className="py-12 bg-gray-200">
          <div className="container mx-auto px-4 text-center">
            <h1
              data-testid="soldadoras-page-title"
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            >
              Renta de <span className="text-orange-500">Soldadoras</span>
            </h1>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Soluciones completas en soldadoras de combustión, eléctricas y mantenimiento
              especializado para tus proyectos industriales.
            </p>
          </div>
        </section>

        {/* Cards */}
        <section className="py-16 bg-gray-100">
          <div className="container mx-auto px-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <article
                  key={service.id}
                  data-testid={`soldadoras-card-${service.id}`}
                  className="group flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-orange-500/50 transition-all duration-300 overflow-hidden"
                >
                  {/* Image container */}
                  <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-200">
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Body */}
                  <div className="flex flex-col flex-1 p-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition-colors">
                      {service.title}
                    </h2>
                    <p className="text-gray-600 mb-6 flex-1">{service.description}</p>

                    <a
                      href={buildWhatsAppHref(service.message)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`soldadoras-cta-${service.id}`}
                      data-analytics-click={`soldadoras-whatsapp-${service.id}`}
                      className="inline-flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 hover:scale-[1.02]"
                    >
                      {/* WhatsApp icon */}
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                      Cotizar por WhatsApp
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="py-12 bg-gray-200">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              ¿Necesitas asesoría para elegir tu <span className="text-orange-500">soldadora</span>?
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto mb-6">
              Nuestro equipo te ayuda a seleccionar el equipo adecuado según la potencia, el tipo de
              material y las condiciones de tu obra.
            </p>
            <a
              href={buildWhatsAppHref(
                'Hola GENSOL, me gustaría asesoría para elegir una soldadora.'
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="soldadoras-cta-advisory"
              data-analytics-click="soldadoras-whatsapp-advisory"
              className="inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105"
            >
              Hablar con un asesor
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default SoldadorasPage;
